import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Mail } from 'lucide-react';
import { freeFetch, inputClass } from './api';
import { useFreeAuth } from './FreeAuth';
import { LegalLinks } from '../legal/LegalLinks';

export default function FreeLanding() {
  const navigate = useNavigate();
  const { status } = useFreeAuth();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (status === 'in') navigate('/free/dashboard', { replace: true });
  }, [status, navigate]);

  // Nothing renders until the session is resolved, and nothing renders while the
  // redirect to the dashboard is in flight — a signed-in lifter must never see
  // this page, not even for a frame.
  if (status !== 'out') {
    return <div className="min-h-screen bg-mono-base" data-testid="free-auth-resolving" />;
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!email || loading) return;
    setLoading(true);
    setError('');
    try {
      const data = await freeFetch('/auth/request-link', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      if (data.ok === false) {
        setError(data.message);
        return;
      }
      setSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 relative" data-testid="free-landing">
      <div className="w-full max-w-lg">
        <span className="text-mono-mute font-heading text-sm font-bold uppercase tracking-[0.3em]">
          Coach Rands Method
        </span>

        {!sent ? (
          <>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white mt-6 mb-4 leading-none">
              Track your weighted pull-up progress.
              <span className="block">Free.</span>
            </h1>
            <p className="text-mono-mute text-base md:text-lg font-body mb-10 leading-relaxed">
              Log your sessions, track your progression, and get weekly check-ins — built for serious calisthenics athletes.
            </p>
            <form onSubmit={submit} className="space-y-4">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address"
                className={inputClass + ' py-3.5'}
                data-testid="free-email-input"
              />
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-white hover:bg-[#E5E5E5] disabled:opacity-50 text-black font-heading font-bold text-lg uppercase tracking-wider rounded-none px-8 py-3.5 transition-all duration-200 active:scale-95 flex items-center justify-center group"
                data-testid="free-start-tracking-btn"
              >
                {loading ? 'Sending...' : 'Start tracking'}
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </button>
              {error && <p className="text-crimson text-sm font-body" data-testid="free-landing-error">{error}</p>}
            </form>
          </>
        ) : (
          <div className="mt-6" data-testid="free-link-sent">
            <div className="border border-mono-line bg-mono-card p-8">
              <Mail className="h-8 w-8 text-white mb-4" />
              <p className="text-white font-body text-lg leading-relaxed" data-testid="free-inbox-message">
                Check your inbox for your access link. It takes about 30 seconds to arrive.
              </p>
            </div>
          </div>
        )}
        <LegalLinks className="mt-12" />
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
    </div>
  );
}
